import { useCallback, useEffect, useState } from "react";
import {
  getGoogleCalendarStatus,
  listEmployeePacks,
  listTenants,
  type EmployeeFacadeProfile,
  type EmployeePackList,
  type GoogleCalendarStatus,
} from "../lib/api";

type TenantOption = { tenant_id: string; bot_name?: string };

const OAUTH_NEXT = "/admin/system?tab=employees";
const CALENDAR_TENANT_KEY = "admin_calendar_tenant";

const MODES = [
  {
    id: "employee-pack",
    title: "Employee Pack＝預設主路徑",
    description: "Pack 定義角色；facade 只暴露指定 Skill Pack；tool executor 負責執行已授權的工具。",
    tone: "border-emerald-700/40 bg-emerald-950/20 text-emerald-300",
  },
  {
    id: "legacy",
    title: "legacy＝回退，不是預設角色",
    description: "未遷移租戶沿用現有客服行為；驗證失敗或 rollback 時亦暫用，完成修復後才切回 Employee Pack。",
    tone: "border-amber-700/40 bg-amber-950/20 text-amber-300",
  },
  {
    id: "integration",
    title: "integration＝不執行 AI",
    description: "僅處理外部系統整合與資料交換，不進入 AI 推論或工具執行鏈。",
    tone: "border-sky-700/40 bg-sky-950/20 text-sky-300",
  },
];

function modeTone(mode: string): string {
  const normalized = mode.toLowerCase().replace(/[_\s]/g, "-");
  if (normalized.includes("legacy")) return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  if (normalized.includes("integration")) return "bg-sky-500/15 text-sky-300 border-sky-500/30";
  return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
}

function formatCalendarTime(value: number | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("zh-HK", { hour12: false });
}

export default function SystemInfoEmployees() {
  const [data, setData] = useState<EmployeePackList | null>(null);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [facadeTenant, setFacadeTenant] = useState("");
  const [facadeProfiles, setFacadeProfiles] = useState<EmployeeFacadeProfile[]>([]);
  const [facadeLoading, setFacadeLoading] = useState(false);
  const [facadeError, setFacadeError] = useState("");
  const [facadeReload, setFacadeReload] = useState(0);
  const [calendarTenant, setCalendarTenant] = useState(() => sessionStorage.getItem(CALENDAR_TENANT_KEY) || "obbx6fj");
  const [calendar, setCalendar] = useState<GoogleCalendarStatus | null>(null);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState("");
  const [calendarNotice, setCalendarNotice] = useState("");
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [result, tenantData] = await Promise.all([listEmployeePacks(), listTenants()]);
      if (!Array.isArray(result.packs)) throw new Error("回應缺 packs");
      if (tenantData.success === false || !Array.isArray(tenantData.tenants)) throw new Error("租戶清單載入失敗");
      setData(result);
      setTenants(tenantData.tenants);
      const customerPackIds = new Set(
        result.packs.filter((pack) => pack.role === "customer_service").map((pack) => pack.packId),
      );
      const customerTenants = (result.tenants ?? []).filter(
        (tenant) => tenant.packId !== null && customerPackIds.has(tenant.packId),
      );
      setFacadeTenant((current) => {
        if (customerTenants.some((tenant) => tenant.tenantId === current)) return current;
        return customerTenants.find((tenant) => tenant.facadeKbEnabled)?.tenantId
          || customerTenants[0]?.tenantId
          || "";
      });
      setFacadeReload((value) => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!facadeTenant) {
      setFacadeProfiles([]);
      setFacadeError("");
      setFacadeLoading(false);
      return;
    }
    setFacadeLoading(true);
    setFacadeError("");
    listEmployeePacks(facadeTenant)
      .then((result) => {
        if (cancelled) return;
        setFacadeProfiles((result.facadeProfiles ?? []).filter((profile) => profile.tenantId === facadeTenant));
      })
      .catch((err) => {
        if (cancelled) return;
        setFacadeProfiles([]);
        setFacadeError(err instanceof Error ? err.message : "Facade profile 載入失敗");
      })
      .finally(() => {
        if (!cancelled) setFacadeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facadeTenant, facadeReload]);

  const loadCalendar = useCallback(async (tenantId: string) => {
    setCalendarLoading(true);
    setCalendarError("");
    try {
      const result = await getGoogleCalendarStatus(tenantId, OAUTH_NEXT);
      setCalendar(result);
    } catch (err) {
      setCalendar(null);
      setCalendarError(err instanceof Error ? err.message : "Google Calendar 狀態載入失敗");
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get("gcal");
    if (oauth) {
      const messages: Record<string, string> = {
        ok: "Google 授權已完成，正在核實連接狀態。",
        denied: "Google 授權未確認，日曆尚未連接。",
        fail: "Google 授權失敗，請重試或聯絡平台管理員。",
        reauth: "Google 授權已失效，請重新連接。",
      };
      setCalendarNotice(messages[oauth] || "Google 授權狀態已更新，正在核實。 ");
      params.delete("gcal");
      window.history.replaceState(null, "", `/admin/system?tab=employees${params.toString() ? `&${params}` : ""}`);
    }
  }, [load]);

  useEffect(() => {
    void loadCalendar(calendarTenant);
  }, [calendarTenant, loadCalendar]);

  async function handleCalendarConnect() {
    setConnecting(true);
    setCalendarError("");
    try {
      const result = await getGoogleCalendarStatus(calendarTenant, OAUTH_NEXT);
      if (!result.connectUrl) throw new Error("平台尚未配置授權連結，請聯絡平台管理員");
      window.location.assign(result.connectUrl);
    } catch (err) {
      setCalendarError(err instanceof Error ? err.message : "授權連結載入失敗");
      setConnecting(false);
    }
  }

  const tenantModes = Object.entries(data?.tenantModes ?? {});
  const customerPackIds = new Set(
    (data?.packs ?? []).filter((pack) => pack.role === "customer_service").map((pack) => pack.packId),
  );
  const facadeTenants = (data?.tenants ?? []).filter(
    (tenant) => tenant.packId !== null && customerPackIds.has(tenant.packId),
  );
  const selectedFacadeTenant = facadeTenants.find((tenant) => tenant.tenantId === facadeTenant);
  const facadePack = data?.packs.find(
    (pack) => pack.role === "customer_service" && pack.packId === selectedFacadeTenant?.packId,
  );
  const facadeRows = (facadePack?.facades ?? []).map((facade) => {
    const profile = facadeProfiles.find(
      (item) => item.tenantId === facadeTenant && item.facadeId === facade.id,
    );
    const ready = Boolean(profile?.ready && profile.hasSystemPrompt && profile.chunkCount > 0);
    return { facade, profile, ready };
  });
  const allFacadesReady = facadeRows.length > 0 && facadeRows.every((row) => row.ready);
  const facadeRunning = allFacadesReady && selectedFacadeTenant?.facadeKbEnabled === true
    && !facadeLoading && !facadeError;
  const facadeStateLabel = facadeLoading
    ? "狀態載入中…"
    : facadeError
      ? "狀態未知"
      : facadeRunning
        ? "運行中"
        : allFacadesReady
          ? "全部就緒，開關未啟用"
          : "尚未就緒（未運行）";

  return (
    <div className="space-y-6">
      <section className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-emerald-400">🧬 AI 員工與 Employee Pack</h2>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">
              以角色 Pack 管理 AI 員工能力，再經 facade 選取 Skill Pack。實際工具動作一律由 tool executor 按 policy 執行。
            </p>
          </div>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="px-3 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? "載入中…" : "↻ 重新載入"}
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {MODES.map((mode) => (
            <div key={mode.id} className={`rounded-lg border p-3 ${mode.tone}`}>
              <h3 className="text-sm font-semibold">{mode.title}</h3>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">{mode.description}</p>
            </div>
          ))}
        </div>
      </section>

      {error && (
        <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">
          無法載入 Employee Pack：{error}
        </div>
      )}

      {!loading && !error && data?.packs.length === 0 && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-8 text-center text-sm text-slate-500">
          尚未設定 Employee Pack。
        </div>
      )}

      {data?.packs.map((pack) => (
        <section key={`${pack.packId}-${pack.version}`} className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3 pb-4 border-b border-slate-800">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-slate-100">{pack.role}</h2>
                <span className={`px-2 py-0.5 rounded-full border text-[11px] font-medium ${modeTone(pack.executionMode)}`}>
                  {pack.executionMode}
                </span>
                <span className="px-2 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/15 text-[11px] text-violet-300">
                  {pack.visibility}
                </span>
              </div>
              <p className="text-xs text-slate-500 font-mono mt-2 break-all">
                {pack.packId} · v{pack.version}
              </p>
            </div>
            <div className="text-xs text-slate-400">
              預設 facade：<span className="text-emerald-300 font-mono">{pack.defaultFacade}</span>
            </div>
          </div>

          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4 text-xs">
            <div className="bg-slate-950/60 rounded-lg border border-slate-800 p-3">
              <dt className="text-slate-500">角色 role</dt>
              <dd className="text-slate-200 mt-1">{pack.role}</dd>
            </div>
            <div className="bg-slate-950/60 rounded-lg border border-slate-800 p-3">
              <dt className="text-slate-500">執行模式 executionMode</dt>
              <dd className="text-slate-200 mt-1 font-mono break-all">{pack.executionMode}</dd>
            </div>
          </dl>

          <h3 className="text-sm font-semibold text-slate-200 mb-3">Facade／Skill Pack</h3>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {(Array.isArray(pack.facades) ? pack.facades : []).map((facade) => (
              <article key={facade.id} className="bg-slate-950/60 rounded-lg border border-slate-800 p-4 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <h4 className="text-sm font-semibold text-emerald-300 font-mono break-all">{facade.id}</h4>
                  {facade.id === pack.defaultFacade && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10px]">預設 facade</span>
                  )}
                </div>
                <dl className="space-y-3 text-xs">
                  <div>
                    <dt className="text-slate-500 mb-1">KB namespace</dt>
                    <dd className="text-slate-300 font-mono break-all">{facade.kbNamespace}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 mb-1">Workflow</dt>
                    <dd className="text-slate-300 whitespace-pre-wrap leading-relaxed">{facade.workflow}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 mb-1">Policy</dt>
                    <dd className="text-slate-300 whitespace-pre-wrap break-all leading-relaxed">{facade.policy}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500 mb-1">Skills</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {Array.isArray(facade.skills) && facade.skills.length > 0
                        ? facade.skills.map((skill) => (
                            <span key={skill} className="px-2 py-1 rounded bg-violet-500/10 border border-violet-500/25 text-violet-300 font-mono">
                              {skill}
                            </span>
                          ))
                        : <span className="text-slate-500">未列出 skills</span>}
                    </dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        </section>
      ))}

      <section className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-emerald-400">三門 KB 就緒狀態</h2>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">
              唯讀檢查各 facade 的獨立知識庫與 prompt；只有三門全部就緒且開關為 1，才會顯示「運行中」。
            </p>
          </div>
          <span className={`px-2.5 py-1 rounded-full border text-xs font-medium ${facadeRunning
            ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
            : "border-amber-500/30 bg-amber-500/15 text-amber-300"}`}
          >
            {facadeStateLabel}
          </span>
        </div>

        {facadeTenants.length === 0 ? (
          <p className="text-sm text-slate-500">尚未有 customer_service Pack 租戶可供檢查。</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end mb-4">
              <div className="min-w-0">
                <label htmlFor="facade-status-tenant" className="block text-xs text-slate-400 mb-1">檢查租戶</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    id="facade-status-tenant"
                    value={facadeTenant}
                    onChange={(event) => setFacadeTenant(event.target.value)}
                    className="w-full min-w-0 flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
                  >
                    {facadeTenants.map((tenant) => (
                      <option key={tenant.tenantId} value={tenant.tenantId}>{tenant.tenantId}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => setFacadeReload((value) => value + 1)}
                    disabled={facadeLoading || !facadeTenant}
                    className="shrink-0 px-3 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                  >
                    {facadeLoading ? "載入中…" : "重新整理"}
                  </button>
                </div>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs">
                <span className="text-slate-500">facadeKbEnabled 實際開關</span>
                <p className={`mt-1 font-mono ${selectedFacadeTenant?.facadeKbEnabled ? "text-emerald-300" : "text-amber-300"}`}>
                  {selectedFacadeTenant?.facadeKbEnabled ? "true（1，開啟）" : "false（0，關閉）"}
                </p>
              </div>
            </div>

            {facadeError && (
              <div role="alert" className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">
                Facade profile 載入失敗：{facadeError}
              </div>
            )}
            {!facadeLoading && !facadeError && facadeProfiles.length === 0 && (
              <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                查無 facade profiles，可能尚未遷移或尚未建立資料；以下各門按未就緒顯示。
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {facadeRows.map(({ facade, profile, ready }) => (
                <article key={facade.id} className="min-w-0 rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-200 break-words">{facade.description || facade.id}</h3>
                      <p className="text-xs text-emerald-300 font-mono mt-1 break-all">{facade.id}</p>
                    </div>
                    <span className={`shrink-0 px-2 py-0.5 rounded-full border text-[11px] ${ready
                      ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
                      : "border-rose-500/30 bg-rose-500/15 text-rose-300"}`}
                    >
                      {ready ? "ready" : "not ready"}
                    </span>
                  </div>
                  <dl className="space-y-2 text-xs">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">KB chunks</dt>
                      <dd className="font-mono text-slate-200">{profile?.chunkCount ?? 0}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">獨立 system prompt</dt>
                      <dd className={profile?.hasSystemPrompt ? "text-emerald-300" : "text-rose-300"}>
                        {profile?.hasSystemPrompt ? "已建立" : "缺少"}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">Profile ready 旗標</dt>
                      <dd className={`font-mono ${profile?.ready ? "text-emerald-300" : "text-rose-300"}`}>
                        {profile?.ready ? "ready=1" : "ready=0"}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">更新時間</dt>
                      <dd className="text-slate-300 text-right">{formatCalendarTime(profile?.updatedAt ?? null)}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      {tenantModes.length > 0 && (
        <section className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
          <h2 className="text-sm font-semibold text-slate-200 mb-3">租戶執行模式（tenantModes）</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {tenantModes.map(([tenantId, mode]) => (
              <div key={tenantId} className="flex items-center justify-between gap-2 rounded-lg bg-slate-950/60 border border-slate-800 p-3 text-xs">
                <span className="text-slate-300 font-mono break-all">{tenantId}</span>
                <span className={`shrink-0 px-2 py-0.5 rounded-full border ${modeTone(mode)}`}>{mode}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-slate-900 rounded-xl border border-slate-800 p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-sky-400">📅 Google Calendar 自助連接</h2>
            <p className="text-sm text-slate-400 mt-1 leading-relaxed">
              不需進入 Google Cloud Console。選擇租戶後按連接，在 Google 確認帳戶及權限，再返回此頁。
            </p>
          </div>
          <button
            onClick={() => void loadCalendar(calendarTenant)}
            disabled={calendarLoading}
            className="px-3 py-2 text-sm rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            {calendarLoading ? "狀態載入中…" : "↻ 重新整理日曆狀態"}
          </button>
        </div>

        {calendarNotice && <p role="status" className="mb-3 rounded-lg border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-sky-200">{calendarNotice}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label htmlFor="calendar-tenant" className="block text-xs text-slate-400 mb-1">要連接的租戶</label>
            <select
              id="calendar-tenant"
              aria-label="要連接的租戶"
              value={calendarTenant}
              onChange={(event) => {
                const nextTenant = event.target.value;
                if (nextTenant === calendarTenant) return;
                sessionStorage.setItem(CALENDAR_TENANT_KEY, nextTenant);
                setCalendarTenant(nextTenant);
                setCalendar(null);
                setCalendarNotice("");
                setCalendarError("");
              }}
              disabled={loading || connecting}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 disabled:opacity-50"
            >
              {tenants.map((tenant) => (
                <option key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.bot_name ? `${tenant.bot_name} · ${tenant.tenant_id}` : tenant.tenant_id}
                </option>
              ))}
            </select>
          </div>
          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3 text-xs text-slate-400">
            {calendarLoading ? "正在讀取日曆狀態…" : calendar?.connected ? (
              <>
                <span className="inline-flex px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 text-emerald-300">已連接</span>
                <span className="ml-2 text-slate-200 break-all">{calendar.googleEmail || "Google 帳戶"}</span>
                <p className="mt-1">連接時間：{formatCalendarTime(calendar.connectedAt)}</p>
              </>
            ) : calendar?.needsReauth ? "授權已失效，需要重新連接。" : "尚未連接 Google Calendar。"}
          </div>
        </div>

        {calendarError && <div role="alert" className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">{calendarError}</div>}
        {!calendarLoading && calendar && !calendar.connectUrl && !calendar.connected && (
          <p className="mt-3 text-xs text-amber-300">平台尚未配置授權連結，請聯絡平台管理員。</p>
        )}

        {!calendar?.connected && (
          <button
            id="calendar-connect"
            onClick={() => void handleCalendarConnect()}
            disabled={connecting || calendarLoading || !calendar?.connectUrl}
            className="mt-4 w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-40"
          >
            {connecting ? "正在前往 Google…" : calendar?.needsReauth ? "重新連接 Google 日曆" : "連接 Google 日曆"}
          </button>
        )}

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mt-5">
          <li className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
            <span className="text-sky-300 font-semibold">1. 按「連接」</span>
            <p className="text-xs text-slate-400 mt-1">系統會取得全新簽名授權連結，避免舊連結過期。</p>
          </li>
          <li className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
            <span className="text-sky-300 font-semibold">2. 完成 Google 授權</span>
            <p className="text-xs text-slate-400 mt-1">在 Google 授權頁確認帳戶與日曆權限，再返回產品。</p>
          </li>
          <li className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
            <span className="text-sky-300 font-semibold">3. 建立測試行程</span>
            <p className="text-xs text-slate-400 mt-1">連接成功後建立測試行程，確認同步與讀寫範圍。</p>
          </li>
        </ol>
      </section>
    </div>
  );
}
